<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import { PictureOutlined } from '@ant-design/icons-vue';
import EditTitleModal from '../EditTitleModal.vue';
import EditDescriptionModal from '../EditDescriptionModal.vue';
import ImageUploadModal from '../ImageUploadModal.vue';

const b = bem('info-cards-module');

const DEFAULT_CARDS = [
  {
    id: 1,
    title: 'Card #1',
    description: 'Card Description',
    image: '',
    imageMobile: '',
  },
  {
    id: 2,
    title: 'Card #2',
    description: 'Card Description',
    image: '',
    imageMobile: '',
  },
  {
    id: 3,
    title: 'Card #3',
    description: 'Card Description',
    image: '',
    imageMobile: '',
  },
  {
    id: 4,
    title: 'Card #4',
    description: 'Card Description',
    image: '',
    imageMobile: '',
  },
];

export default {
  components: {
    PencilIcon,
    EditTitleModal,
    EditDescriptionModal,
    ImageUploadModal,
    PictureOutlined,
  },
  props: {
    moduleData: {
      type: Object,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      default: true,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    isPopup: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update'],
  data() {
    return {
      isTitleModalVisible: false,
      isDescriptionModalVisible: false,
      isCardImageModalVisible: false,
      isCardTitleModalVisible: false,
      isCardDescriptionModalVisible: false,
      activeCardIndex: null,
    };
  },
  computed: {
    content() {
      if (this.moduleData?.parsedContent) {
        return this.moduleData.parsedContent;
      }
      if (!this.moduleData?.content) return {};
      if (typeof this.moduleData.content === 'string') {
        try {
          return JSON.parse(this.moduleData.content);
        } catch {
          return {};
        }
      }
      return this.moduleData.content;
    },
    displayTitle() {
      if (this.isMobile && this.content.titleMobile) {
        return this.content.titleMobile;
      }
      return this.content.title || 'Title';
    },
    displayDescription() {
      if (this.isMobile && this.content.descriptionMobile) {
        return this.content.descriptionMobile;
      }
      return this.content.description || 'Description';
    },
    cards() {
      return this.content.cards && this.content.cards.length > 0
        ? this.content.cards
        : DEFAULT_CARDS;
    },
    activeCard() {
      if (this.activeCardIndex === null) return null;
      return this.cards[this.activeCardIndex] || null;
    },
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    openTitleModal() {
      if (this.isEditMode) {
        this.isTitleModalVisible = true;
      }
    },
    openDescriptionModal() {
      if (this.isEditMode) {
        this.isDescriptionModalVisible = true;
      }
    },
    openCardImageModal(index) {
      if (this.isEditMode) {
        this.activeCardIndex = index;
        this.isCardImageModalVisible = true;
      }
    },
    openCardTitleModal(index) {
      if (this.isEditMode) {
        this.activeCardIndex = index;
        this.isCardTitleModalVisible = true;
      }
    },
    openCardDescriptionModal(index) {
      if (this.isEditMode) {
        this.activeCardIndex = index;
        this.isCardDescriptionModalVisible = true;
      }
    },
    handleTitleSave(data) {
      this.updateContent({
        ...this.content,
        title: data.title,
        titleMobile: data.titleMobile,
      });
      this.isTitleModalVisible = false;
    },
    handleDescriptionSave(data) {
      this.updateContent({
        ...this.content,
        description: data.description,
        descriptionMobile: data.descriptionMobile,
      });
      this.isDescriptionModalVisible = false;
    },
    handleCardImageSave(data) {
      const updatedCards = [...this.cards];
      updatedCards[this.activeCardIndex] = {
        ...updatedCards[this.activeCardIndex],
        image: data.image,
        imageMobile: data.imageMobile,
      };
      this.updateContent({
        ...this.content,
        cards: updatedCards,
      });
      this.isCardImageModalVisible = false;
      this.activeCardIndex = null;
    },
    handleCardTitleSave(data) {
      const updatedCards = [...this.cards];
      updatedCards[this.activeCardIndex] = {
        ...updatedCards[this.activeCardIndex],
        title: data.title,
        titleMobile: data.titleMobile,
      };
      this.updateContent({
        ...this.content,
        cards: updatedCards,
      });
      this.isCardTitleModalVisible = false;
      this.activeCardIndex = null;
    },
    handleCardDescriptionSave(data) {
      const updatedCards = [...this.cards];
      updatedCards[this.activeCardIndex] = {
        ...updatedCards[this.activeCardIndex],
        description: data.description,
        descriptionMobile: data.descriptionMobile,
      };
      this.updateContent({
        ...this.content,
        cards: updatedCards,
      });
      this.isCardDescriptionModalVisible = false;
      this.activeCardIndex = null;
    },
    updateContent(newContent) {
      this.$emit('update', {
        ...this.moduleData,
        content: JSON.stringify(newContent),
      });
    },
    getCardImage(card) {
      if (this.isMobile && card.imageMobile) {
        return card.imageMobile;
      }
      return card.image || '';
    },
    getCardTitle(card) {
      if (this.isMobile && card.titleMobile) {
        return card.titleMobile;
      }
      return card.title || 'Card Title';
    },
    getCardDescription(card) {
      if (this.isMobile && card.descriptionMobile) {
        return card.descriptionMobile;
      }
      return card.description || 'Card Description';
    },
  },
};
</script>

<template>
  <div :class="b({ mobile: isMobile, popup: isPopup })">
    <div
      :class="b('title-row', { editable: isEditMode })"
      role="button"
      tabindex="0"
      @click="openTitleModal"
      @keydown.enter="openTitleModal"
    >
      <span :class="b('title', { mobile: isMobile })">{{ displayTitle }}</span>
      <PencilIcon v-if="isEditMode" :size="20" color="#242424" :class="b('edit-icon')" />
    </div>

    <div
      :class="b('desc-row', { editable: isEditMode })"
      role="button"
      tabindex="0"
      @click="openDescriptionModal"
      @keydown.enter="openDescriptionModal"
    >
      <span :class="b('desc', { mobile: isMobile })">{{ displayDescription }}</span>
      <PencilIcon v-if="isEditMode" :size="16" color="#595959" :class="b('edit-icon')" />
    </div>

    <div :class="b('cards-wrap', { mobile: isMobile, popup: isPopup })">
      <div
        v-for="(card, index) in cards"
        :key="card.id || index"
        :class="b('card', { mobile: isMobile, popup: isPopup })"
      >
        <div
          :class="b('card-image', { mobile: isMobile, editable: isEditMode, popup: isPopup })"
          :style="getCardImage(card) ? {
            backgroundImage: `url(${getCardImage(card)})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          } : {}"
          role="button"
          tabindex="0"
          @click="openCardImageModal(index)"
          @keydown.enter="openCardImageModal(index)"
        >
          <span
            v-if="!getCardImage(card) && !isPopup"
            :class="b('card-placeholder', { mobile: isMobile })"
          >
            <span :class="b('card-placeholder-plus', { mobile: isMobile })">+</span>
            <br>
            CLICK TO UPLOAD
          </span>
          <span
            v-if="!getCardImage(card) && isPopup"
            :class="b('card-placeholder', { mobile: isMobile })"
          >
            <PictureOutlined />
          </span>
          <span v-if="isMobile" :class="b('card-title', { mobile: isMobile, popup: isPopup })">
            {{ getCardTitle(card) }}
          </span>
        </div>

        <div
          v-if="!isMobile"
          :class="b('card-title-row', { editable: isEditMode, popup: isPopup })"
          role="button"
          tabindex="0"
          @click="openCardTitleModal(index)"
          @keydown.enter="openCardTitleModal(index)"
        >
          <span :class="b('card-title', { popup: isPopup })">{{ getCardTitle(card) }}</span>
          <PencilIcon v-if="isEditMode" :size="16" color="#242424" :class="b('edit-icon')" />
        </div>

        <div
          :class="b('card-desc-row', { mobile: isMobile, editable: isEditMode, popup: isPopup })"
          role="button"
          tabindex="0"
          @click="openCardDescriptionModal(index)"
          @keydown.enter="openCardDescriptionModal(index)"
        >
          <span :class="b('card-desc', { mobile: isMobile, popup: isPopup })">
            {{ getCardDescription(card) }}
          </span>
          <PencilIcon v-if="isEditMode" :size="14" color="#595959" :class="b('edit-icon')" />
        </div>
      </div>
    </div>

    <EditTitleModal
      :visible="isTitleModalVisible"
      :title="content.title"
      :title-mobile="content.titleMobile"
      @close="isTitleModalVisible = false"
      @save="handleTitleSave"
    />

    <EditDescriptionModal
      :visible="isDescriptionModalVisible"
      :description="content.description"
      :description-mobile="content.descriptionMobile"
      @close="isDescriptionModalVisible = false"
      @save="handleDescriptionSave"
    />

    <ImageUploadModal
      :visible="isCardImageModalVisible"
      :image="activeCard?.image"
      :image-mobile="activeCard?.imageMobile"
      @close="isCardImageModalVisible = false; activeCardIndex = null"
      @save="handleCardImageSave"
    />

    <EditTitleModal
      :visible="isCardTitleModalVisible"
      :title="activeCard?.title"
      :title-mobile="activeCard?.titleMobile"
      @close="isCardTitleModalVisible = false; activeCardIndex = null"
      @save="handleCardTitleSave"
    />

    <EditDescriptionModal
      :visible="isCardDescriptionModalVisible"
      :description="activeCard?.description"
      :description-mobile="activeCard?.descriptionMobile"
      @close="isCardDescriptionModalVisible = false; activeCardIndex = null"
      @save="handleCardDescriptionSave"
    />
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables';
@import '@/styles/media';

.info-cards-module {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 28px;
  padding: 64px 0;

  @include mediaMinMax(1025, $normal) {
    gap: vw-full(28);
    padding: vw-full(64) 0;
  }
  &_mobile {
    gap: 20px;
    padding: 40px 0;
  }

  &__title-row,
  &__desc-row {
    display: flex;
    align-items: center;
    gap: 8px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(8);
    }

    &_editable {
      cursor: pointer;
      padding: 4px 8px;
      border-radius: 4px;
      transition: background 0.2s ease;

      &:hover {
        background: rgba(0, 0, 0, 0.05);
      }
    }
  }

  &__title {
    font-family: Montserrat, sans-serif;
    font-size: 40px;
    font-weight: 600;
    color: #293241;
    text-align: center;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(40);
    }
    &_mobile {
      font-size: 24px;
    }
  }

  &__desc {
    font-family: Montserrat, sans-serif;
    font-size: 19px;
    font-weight: 400;
    color: #5e5d5d;
    text-align: center;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(19);
    }
    &_mobile {
      font-size: 14px;
    }
  }

  &__edit-icon {
    flex-shrink: 0;
  }

  &__cards-wrap {
    width: 1540px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: 24px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(1540);
      gap: vw-full(24);
    }

    &_popup {
      width: 1200px;
      gap: 16px;

      @include mediaMinMax(1025, $normal) {
        width: vw-full(1200);
        gap: vw-full(16);
      }
    }
    &_mobile {
      flex-direction: column;
      width: 100%;
      gap: 40px;
    }
  }

  &__card {
    width: 368px;
    height: 456px;
    border-radius: 3px 18px;
    background: #fff;
    box-shadow: 4px 4px 8px 0 rgba(95, 138, 164, 0.2);
    padding: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 20px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(368);
      height: vw-full(456);
      padding: vw-full(10);
      gap: vw-full(20);
      border-radius: vw-full(3) vw-full(18);
    }

    &_popup {
      width: 286px;
      height: 354px;
      padding: 8px;
      box-shadow: initial;
      border: 1px solid #ddd;

      @include mediaMinMax(1025, $normal) {
        width: vw-full(286);
        height: vw-full(354);
        padding: vw-full(8);
      }
    }
    &_mobile {
      width: 100%;
      height: auto;
      padding: 0;
      border-radius: 0;
      gap: 16px;
      box-shadow: initial;
      background: transparent;
    }
  }

  &__card-image {
    position: relative;
    width: 348px;
    height: 274px;
    flex-shrink: 0;
    border-radius: 3px 12px;
    background: #d2d2d2;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s ease;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(348);
      height: vw-full(274);
      border-radius: vw-full(3) vw-full(12);
    }

    &_popup {
      width: 271px;
      height: 213px;

      @include mediaMinMax(1025, $normal) {
        width: vw-full(271);
        height: vw-full(213);
      }
    }

    &_editable:hover {
      background: #c5c5c5;
    }
    &_mobile {
      width: 100%;
      height: 260px;
      border-radius: 0 20px 0 0;
    }
  }

  &__card-placeholder {
    font-family: Montserrat, sans-serif;
    font-size: 24px;
    font-weight: 400;
    color: #595959;
    text-align: center;
    line-height: 1.4;
    text-transform: uppercase;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(24);
    }
    &_mobile {
      font-size: 18px;
    }
  }

  &__card-placeholder-plus {
    font-size: 48px;
    line-height: 1;
    font-weight: 200;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(48);
    }

    &_mobile {
      font-size: 40px;
    }
  }

  &__card-title-row,
  &__card-desc-row {
    display: flex;
    align-items: center;
    gap: 6px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(6);
    }

    &_editable {
      cursor: pointer;
      transition: background 0.2s ease;

      &:hover {
        background: rgba(0, 0, 0, 0.05);
      }
    }
  }

  &__card-title {
    max-width: 350px;
    font-family: Montserrat, sans-serif;
    font-size: 36px;
    font-weight: 500;
    color: #293241;
    text-align: center;
    text-transform: capitalize;

    @include mediaMinMax(1025, $normal) {
      max-width: vw-full(350);
      font-size: vw-full(36);
    }

    &_popup {
      max-width: 271px;
      font-size: 24px;
      line-height: 1;

      @include mediaMinMax(1025, $normal) {
        max-width: vw-full(271);
        font-size: vw-full(24);
      }
    }
    &_mobile {
      width: 100%;
      font-size: 24px;
      position: absolute;
      bottom: 0;
      font-weight: 600;
      text-transform: uppercase;
      text-align: left;
    }
  }

  &__card-desc {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    font-family: Montserrat, sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #5e5d5d;
    max-width: 350px;
    height: 68px;
    overflow: hidden;

    @include mediaMinMax(1025, $normal) {
      max-width: vw-full(350);
      height: vw-full(68);
      font-size: vw-full(16);
    }

    &_popup {
      max-width: 271px;
      height: initial;

      @include mediaMinMax(1025, $normal) {
        max-width: vw-full(271);
      }
    }
    &_mobile {
      max-width: 100%;
      font-size: 14px;
      height: auto;
    }
  }
}
</style>
